import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { DialogService } from '@progress/kendo-angular-dialog';
import { saveAs } from 'file-saver';
import { AppService } from '../../app.service';
import { DocumentSender } from '../../shared/components/document-sender/document-sender.component';
import { Attachment } from '../../shared/models/attachment';
import { AttachmentsService } from '../../shared/services/attachments.service';

@Component({
	selector: 'lead-attachments',
	templateUrl: './lead-attachments.component.html',
	styleUrls: ['./lead-attachments.component.scss'],
})
export class LeadAttachmentsComponent implements OnChanges, OnDestroy {
	@Input() attachments: Attachment[] = [];

	imageExtensions: string[] = ['.png', '.jpg', '.jpeg'];
	allowedToShowPopupExtensions: string[] = ['.png', '.jpg', '.jpeg', '.pdf'];
	filesToShow: Attachment[] = [];
	preparingFiles = false;
	prepareFilesPromise: Promise<void> | null = null;
	thumbnailUrls: { [attachmentId: number]: string } = {};

	constructor(
		private appService: AppService,
		private attachmentsService: AttachmentsService,
		private dialogService: DialogService
	) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.attachments) {
			this.clearThumbnailUrls();
			this.filesToShow = [];
			this.preparingFiles = false;
			this.prepareFilesPromise = null;
			this.loadThumbnails();
		}
	}

	ngOnDestroy(): void {
		this.clearThumbnailUrls();
	}

	isImageExtension(fileName: string): boolean {
		if (!fileName) {
			return false;
		}

		const name = fileName.toLowerCase();
		return this.imageExtensions.some(ext => name.endsWith(ext));
	}

	getByFileImageClassByExtension(fileName: string): string {
		if (!fileName) {
			return 'fa-file';
		}

		const extension = fileName.toLowerCase().slice(fileName.lastIndexOf('.'));
		let imageClass = 'fa-file';

		switch (extension) {
			case '.xls':
			case '.xlsx':
				imageClass = 'fa-file-excel';
				break;
			case '.doc':
			case '.docx':
				imageClass = 'fa-file-word';
				break;
			case '.pdf':
				imageClass = 'fa-file-pdf';
				break;
			case '.zip':
			case '.rar':
				imageClass = 'fa-file-archive';
				break;
			case '.ppt':
			case '.pptx':
				imageClass = 'fa-file-powerpoint';
				break;
		}

		return imageClass;
	}

	downloadFile(attachment: Attachment): void {
		this.attachmentsService.getAttachmentById(attachment.attachmentId).subscribe(blob => {
			saveAs(blob, attachment.attachmentName, { type: 'application/octet-stream' });
		});
	}

	getThumbnailUrl(attachmentId: number): string {
		return this.thumbnailUrls[attachmentId];
	}

	isAllowedToShowPopupExtension(extension: string): boolean {
		if (!extension) {
			return false;
		}

		const normalizedExtension = extension.toLowerCase();
		return this.allowedToShowPopupExtensions.some(ext => ext === normalizedExtension);
	}

	async openPreview(attachment: Attachment): Promise<void> {
		if (!this.isAllowedToShowPopupExtension(attachment.attachmentExtension)) {
			return;
		}

		await this.ensureFilesPrepared();

		const dialogRef = this.dialogService.open({
			content: DocumentSender,
			title: 'Изображение',
			width: this.appService.isMobileDevice ? '90%' : '90%',
			height: '90%',
		});

		const documentSender = dialogRef.content.instance as DocumentSender;
		documentSender.fileId = attachment.attachmentId;
		documentSender.files = this.filesToShow;
		documentSender.doNotPrepareFiles = true;
		documentSender.hideSendButton = true;
	}

	private async ensureFilesPrepared(): Promise<void> {
		if (this.filesToShow.length > 0) {
			return;
		}

		if (this.prepareFilesPromise) {
			await this.prepareFilesPromise;
			return;
		}

		this.prepareFilesPromise = this.prepareFilesInternal();
		await this.prepareFilesPromise;
	}

	private async prepareFilesInternal(): Promise<void> {
		this.preparingFiles = true;

		try {
			const files = this.attachments.filter(x => this.isAllowedToShowPopupExtension(x.attachmentExtension));
			const preparedFiles: Attachment[] = [];

			for (const file of files) {
				const blob = await this.attachmentsService.getAttachmentById(file.attachmentId).toPromise();

				await new Promise(resolve => {
					const reader = new FileReader();
					reader.readAsDataURL(blob);
					reader.onloadend = () => {
						const preparedFile = new Attachment();
						preparedFile.attachmentId = file.attachmentId;
						preparedFile.attachmentName = file.attachmentName;
						preparedFile.attachmentExtension = file.attachmentExtension;
						preparedFile.attachmentTypeName = file.attachmentTypeName;
						preparedFile.s3FileName = file.s3FileName;
						preparedFile.fileBase64 = this.getPreparedFileBase64(reader.result, file.attachmentExtension);
						preparedFiles.push(preparedFile);
						resolve(reader.result);
					};
				});
			}

			this.filesToShow = preparedFiles;
		} finally {
			this.preparingFiles = false;
			this.prepareFilesPromise = null;
		}
	}

	private getPreparedFileBase64(result: string | ArrayBuffer, extension: string): string {
		const normalizedExtension = extension ? extension.toLowerCase() : '';
		const base64data = (result as string).split(',')[1];

		if (normalizedExtension === '.pdf') {
			return result as string;
		}

		return `data:image/png;base64,${base64data}`;
	}

	private loadThumbnails(): void {
		const imageAttachments = this.attachments.filter(x => this.isImageExtension(x.attachmentName));
		for (const attachment of imageAttachments) {
			this.attachmentsService.getAttachmentById(attachment.attachmentId).subscribe(blob => {
				const reader = new FileReader();
				reader.readAsDataURL(blob);
				reader.onloadend = () => {
					this.thumbnailUrls[attachment.attachmentId] = this.toImageDataUrl(reader.result as string, attachment.attachmentExtension);
				};
			});
		}
	}

	private clearThumbnailUrls(): void {
		this.thumbnailUrls = {};
	}

	private toImageDataUrl(dataUrl: string, extension: string): string {
		const base64data = dataUrl.split(',')[1];
		const normalizedExtension = extension ? extension.toLowerCase() : '';
		let mime = 'image/png';

		if (normalizedExtension === '.jpg' || normalizedExtension === '.jpeg') {
			mime = 'image/jpeg';
		}

		return `data:${mime};base64,${base64data}`;
	}
}
